import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Upload, Shield, Sparkles, Globe2, Heart, ArrowRight, Film } from "lucide-react";
import { api } from "../lib/api";

const HERO_IMG = "https://images.pexels.com/photos/12013504/pexels-photo-12013504.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CREATOR_IMG = "https://images.unsplash.com/photo-1576280314550-773c50583407?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxmaWxtbWFrZXIlMjBzaWxob3VldHRlJTIwY2FtZXJhfGVufDB8fHx8MTc4MTk4MDg2OHww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [featured, setFeatured] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    api.get("/movies/featured?limit=3")
      .then((r) => { if (!cancelled) setFeatured(r.data || []); })
      .catch(() => { if (!cancelled) setFeatured([]); });
    return () => { cancelled = true; };
  }, []);
  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0A]" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-32 lg:pt-36 lg:pb-48">
          <div className="max-w-3xl animate-fade-up" style={{ opacity: 0 }}>
            <p className="text-eyebrow mb-6 flex items-center gap-2" data-testid="hero-eyebrow">
              <Sparkles className="h-3.5 w-3.5 text-[#EAB308]" />
              African Christian Cinema · Streaming Reborn
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tighter leading-[0.95]" data-testid="hero-title">
              Stories worth <span className="italic text-[#E65F2B]">believing</span>.
              <br />
              Cinema worth <span className="italic text-[#EAB308]">sharing</span>.
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-white/70 max-w-xl leading-relaxed" data-testid="hero-subtitle">
              Christoreel is the home of African Christian movies — a streaming platform where filmmakers
              build channels, audiences discover Light-filled stories, and every reel honors the One who
              wrote the first one.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/register" className="ember-btn text-base" data-testid="hero-cta-primary">
                Start Watching · from ₦2,000/mo <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/browse" className="ghost-btn text-base" data-testid="hero-cta-browse">
                Browse the Library
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-white/40">
              <span>● Cancel anytime</span>
              <span>● Naira native</span>
              <span>● Curated. Christian. African.</span>
            </div>
          </div>
        </div>
      </section>

      {/* BENTO STRIP */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 -mt-16 relative z-10">
        <div className="grid grid-cols-12 gap-4 lg:gap-6">
          {/* Featured movie trio (admin-curated) */}
          <div className="col-span-12 lg:col-span-8 grid grid-cols-3 gap-4" data-testid="featured-trio">
            {featured === null ? (
              // Loading skeletons
              [0, 1, 2].map((i) => (
                <div key={i} className="relative aspect-[2/3] overflow-hidden rounded-xl border border-white/5 bg-white/[0.03] animate-pulse" />
              ))
            ) : featured.length === 0 ? (
              // Empty state — no featured yet
              <div className="col-span-3 aspect-[2/3] sm:aspect-auto sm:min-h-[420px] rounded-xl border border-dashed border-white/10 bg-white/[0.02] flex flex-col items-center justify-center p-10 text-center" data-testid="featured-empty">
                <Film className="h-8 w-8 text-white/30 mb-4" />
                <p className="text-eyebrow mb-2">Featured films · coming soon</p>
                <p className="text-sm text-white/50 max-w-sm">
                  Our editorial team is curating the first slate of African Christian cinema. New titles arrive weekly.
                </p>
                <Link to="/browse" className="ghost-btn !py-2 !px-4 mt-6 text-sm" data-testid="featured-empty-browse">
                  Browse the library <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            ) : (
              // Real featured movies
              featured.slice(0, 3).map((m) => {
                const poster = m.cloudflare_thumbnail_url || m.poster_path || "";
                return (
                  <Link
                    key={m.id}
                    to={`/movies/${m.id}`}
                    className="relative aspect-[2/3] overflow-hidden rounded-xl border border-white/5 group block"
                    data-testid={`featured-movie-${m.id}`}
                  >
                    {poster ? (
                      <img src={poster} alt={m.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center">
                        <Film className="h-10 w-10 text-white/20" />
                      </div>
                    )}
                    <div className="absolute inset-0 poster-overlay" />
                    <div className="absolute top-3 left-3">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#E65F2B]/90 backdrop-blur-sm text-[10px] uppercase tracking-widest font-bold text-white">
                        <Sparkles className="h-3 w-3" />
                        Featured
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-[10px] uppercase tracking-widest text-[#EAB308]">{m.genre || "Drama"}</p>
                      <p className="font-display font-bold text-white truncate">{m.title}</p>
                      {m.channel_name && (
                        <p className="text-xs text-white/60 truncate mt-0.5">{m.channel_name}</p>
                      )}
                    </div>
                  </Link>
                );
              })
            )}
          </div>

          {/* Stats card */}
          <div className="col-span-12 lg:col-span-4 card-surface p-8 flex flex-col justify-between">
            <div>
              <p className="text-eyebrow">By the numbers</p>
              <div className="mt-6 space-y-6">
                <div>
                  <p className="font-display text-5xl font-black text-white">100%</p>
                  <p className="text-sm text-white/50 mt-1">Christian-curated content</p>
                </div>
                <div>
                  <p className="font-display text-5xl font-black text-[#E65F2B]">54</p>
                  <p className="text-sm text-white/50 mt-1">African countries welcomed</p>
                </div>
                <div>
                  <p className="font-display text-5xl font-black text-[#EAB308]">from ₦2,000</p>
                  <p className="text-sm text-white/50 mt-1">Monthly · 4 quality tiers · No ads</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid grid-cols-12 gap-12 items-end mb-16">
          <div className="col-span-12 lg:col-span-7">
            <p className="text-eyebrow mb-3">Why Christoreel</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
              Built for the audience. <br />
              <span className="text-white/40">Built for the storyteller.</span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Play, color: "#E65F2B", title: "Stream without compromise", desc: "Every title is hand-reviewed by our moderation team. No surprises. No spiritual junk food. Just stories that build." },
            { icon: Upload, color: "#EAB308", title: "Channels for creators", desc: "Filmmakers register, claim a channel name, and upload directly. Your library, your name, your audience." },
            { icon: Globe2, color: "#F97316", title: "Naira-first, Africa-wide", desc: "Built around Paystack. Native NGN pricing. Pan-African distribution. No painful currency math." },
          ].map((p, i) => (
            <div key={i} className="card-surface p-8 group hover:-translate-y-1 transition-all duration-300" style={{ animationDelay: `${i * 100}ms` }}>
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg" style={{ background: `${p.color}1A`, border: `1px solid ${p.color}40` }}>
                <p.icon className="h-6 w-6" style={{ color: p.color }} />
              </div>
              <h3 className="font-display text-xl font-bold mt-6">{p.title}</h3>
              <p className="text-sm text-white/60 mt-3 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CREATOR CALLOUT */}
      <section className="relative overflow-hidden border-y border-white/5">
        <div className="absolute inset-0">
          <img src={CREATOR_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid grid-cols-12 gap-12">
          <div className="col-span-12 lg:col-span-7">
            <p className="text-eyebrow mb-4 flex items-center gap-2"><Upload className="h-3.5 w-3.5 text-[#E65F2B]" /> For Filmmakers</p>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-tight">
              Your channel. <br /> Your library. <br /> <span className="text-[#E65F2B]">Your calling.</span>
            </h2>
            <p className="mt-6 text-lg text-white/70 max-w-xl">
              Register as a creator. Define your channel name. Upload MP4 movies up to 500MB.
              Our admin team reviews every title for Christian appropriateness — then it goes live to subscribers across Africa.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/register" className="ember-btn" data-testid="creator-cta">
                Open Your Channel <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/login" className="ghost-btn" data-testid="creator-cta-login">I already have an account</Link>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING TEASE — 4 quality tiers */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="text-center mb-12">
          <p className="text-eyebrow mb-3">Simple, honest membership</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
            Four plans. <span className="text-[#E65F2B]">One catalogue.</span>
          </h2>
          <p className="mt-4 text-white/60 max-w-xl mx-auto">
            Pick the picture quality that fits your screen and your wallet. Every plan unlocks every approved film. Cancel anytime.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
          {[
            { name: "SD 480p",       label: "OK",    price: "₦2,000", tag: "Mobile & phones",     color: "#94A3B8" },
            { name: "HD 720p",       label: "Good",  price: "₦3,000", tag: "Phones & tablets",    color: "#38BDF8" },
            { name: "Full HD 1080p", label: "Great", price: "₦4,000", tag: "Living-room TVs",     color: "#EAB308" },
            { name: "4K UHD HDR",    label: "Best",  price: "₦5,000", tag: "Cinema-grade", featured: true,  color: "#E65F2B" },
          ].map((p) => (
            <div
              key={p.name}
              className={`card-surface p-5 sm:p-6 relative overflow-hidden ${p.featured ? "!border-[#E65F2B]/40" : ""}`}
              data-testid={`landing-tier-${p.label.toLowerCase()}`}
            >
              {p.featured && (
                <div className="absolute top-0 right-0 text-[10px] font-bold tracking-widest text-[#E65F2B] bg-[#E65F2B]/10 border-l border-b border-[#E65F2B]/30 px-2 py-1 rounded-bl-lg">
                  BEST
                </div>
              )}
              <p className="text-eyebrow" style={{ color: p.color }}>{p.label}</p>
              <p className="font-display text-lg sm:text-xl font-black mt-1">{p.name}</p>
              <p className="text-xs text-white/40 mt-1">{p.tag}</p>
              <p className="font-display text-3xl sm:text-4xl font-black mt-4">
                {p.price}
                <span className="text-xs text-white/40 font-medium ml-1">/mo</span>
              </p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to="/register" className="ember-btn justify-center" data-testid="pricing-cta">
            Start membership <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/subscription" className="ghost-btn justify-center" data-testid="pricing-compare-cta">
            Compare all four plans
          </Link>
        </div>
        <div className="mt-6 flex items-center justify-center gap-3 text-xs text-white/40">
          <Heart className="h-3 w-3 text-[#E65F2B]" fill="#E65F2B" />
          <span>Every plan unlocks every approved film · Cancel anytime · Paystack secure checkout</span>
        </div>
      </section>
    </div>
  );
}
